import React from 'react';
import { useQuery } from 'react-query';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import PageHeader from '../components/ui/PageHeader';
import { FORMA_LINK_BTN } from '../utils/listPageUi';
import { getDashboardChartTokens } from '../utils/chartTheme';
import { dashboardAPI } from '../services/api';
import {
  BanknotesIcon,
  BuildingLibraryIcon,
  ClipboardDocumentCheckIcon,
  AcademicCapIcon,
  CalendarDaysIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';
import {
  DashboardStatCard,
  DashboardPanel,
  DashboardDistributionCard,
  DashboardCourseRow,
  DashboardEmptyState,
} from '../components/dashboard/DashboardSections';

const linkSubtle =
  'text-sm font-medium text-slate-600 underline-offset-4 hover:text-forma-700 hover:underline dark:text-slate-400 dark:hover:text-forma-400';

const Dashboard = () => {
  const { isDark } = useTheme();
  const { language, t } = useLanguage();
  const chart = React.useMemo(() => getDashboardChartTokens(isDark), [isDark]);

  const { data: formationSnap, isLoading } = useQuery(
    ['dashboardFormationSnapshot'],
    () => dashboardAPI.getFormationSnapshot(),
    { staleTime: 60 * 1000 }
  );

  const dashboardData = formationSnap?.dashboardData;
  const courseDistribution = formationSnap?.courseDistribution ?? [];
  const occupancyData = formationSnap?.occupancyData ?? [];
  const revenueChartData = formationSnap?.revenueChartData ?? [];
  const upcomingCoursesData = formationSnap?.upcomingCoursesData ?? [];

  const mainStats = React.useMemo(() => {
    const data = dashboardData?.data || {};

    return [
      {
        title: t('dashboard.statStudents'),
        value: String(data.members?.active ?? '0'),
        change: `${data.members?.growthRate > 0 ? '+' : ''}${data.members?.growthRate ?? 0}%`,
        positive: data.members?.growthRate >= 0,
        icon: AcademicCapIcon,
        iconWrap: 'bg-forma-50 text-forma-700 dark:bg-forma-950/50 dark:text-forma-400',
        accent: 'dashboard-stat--forma',
        link: '/students',
      },
      {
        title: t('dashboard.statCourses'),
        value: String(data.courses?.active ?? '0'),
        change: `+${data.courses?.newThisMonth ?? 0}`,
        positive: true,
        icon: ClipboardDocumentCheckIcon,
        iconWrap: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
        accent: 'dashboard-stat--emerald',
        link: '/courses',
      },
      {
        title: t('dashboard.statRevenue'),
        value: `${(data.revenue?.thisMonth ?? 0).toLocaleString()} ${t('dashboard.currencyDa')}`,
        change: `${data.revenue?.growthRate > 0 ? '+' : ''}${data.revenue?.growthRate ?? 0}%`,
        positive: data.revenue?.growthRate >= 0,
        icon: BanknotesIcon,
        iconWrap: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
        accent: 'dashboard-stat--slate',
        link: '/benefits',
      },
      {
        title: t('dashboard.statOccupancy'),
        value: `${data.classrooms?.occupancyRate ?? 0}%`,
        change: `${data.classrooms?.occupancyGrowth > 0 ? '+' : ''}${data.classrooms?.occupancyGrowth ?? 0}%`,
        positive: data.classrooms?.occupancyGrowth >= 0,
        icon: BuildingLibraryIcon,
        iconWrap: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
        accent: 'dashboard-stat--amber',
        link: '/classrooms',
      },
    ];
  }, [dashboardData, t]);

  const formationDistribution = React.useMemo(() => {
    if (courseDistribution.length === 0) {
      return [{ name: t('dashboard.noCourseData'), count: 0, barClass: 'bg-slate-300 dark:bg-slate-600' }];
    }

    return courseDistribution
      .sort((a, b) => b.value - a.value)
      .slice(0, 3)
      .map((item, index) => ({
        name: item.name,
        count: item.value,
        barClass: ['bg-forma-600', 'bg-emerald-600', 'bg-slate-500'][index],
      }));
  }, [courseDistribution, t]);

  const totalCourses = React.useMemo(() => {
    return courseDistribution.reduce((sum, c) => sum + c.value, 0) || 1;
  }, [courseDistribution]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat(language === 'ar' ? 'ar-DZ' : 'fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const tooltipStyles = {
    backgroundColor: chart.tooltipBg,
    borderColor: chart.tooltipBorder,
    borderRadius: 8,
  };

  return (
    <div className="dashboard-page space-y-6 pb-2">
      <PageHeader
        title={t('dashboard.title')}
        description={t('dashboard.description')}
        actions={
          <Link to="/courses/add" className={FORMA_LINK_BTN}>
            <PlusIcon className="h-5 w-5" aria-hidden />
            {t('dashboard.newCourse')}
          </Link>
        }
      />

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {mainStats.map((stat) => (
          <DashboardStatCard
            key={stat.title}
            stat={stat}
            isLoading={isLoading}
            detailsLabel={t('dashboard.seeDetails')}
          />
        ))}
      </section>

      <DashboardPanel
        title={t('dashboard.chartDistributionTitle')}
        actions={
          <Link to="/courses" className={linkSubtle}>
            {t('dashboard.allCoursesLink')}
          </Link>
        }
      >
        <div className="grid gap-5 sm:grid-cols-3">
          {formationDistribution.map((item, index) => {
            const pct = totalCourses ? Number(((item.count / totalCourses) * 100).toFixed(1)) : 0;
            return (
              <DashboardDistributionCard
                key={`${item.name}-${index}`}
                item={item}
                pct={pct}
                shareLabel={t('dashboard.shareOfCourses', { pct })}
              />
            );
          })}
        </div>
      </DashboardPanel>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <DashboardPanel
          title={t('dashboard.revenueTitle')}
          actions={
            <Link to="/benefits" className={linkSubtle}>
              {t('dashboard.benefitsLink')}
            </Link>
          }
        >
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueChartData} margin={{ top: 8, right: 4, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.muted} vertical={false} />
                <XAxis dataKey="name" stroke={chart.axis} tickLine={false} axisLine={false} fontSize={11} />
                <YAxis
                  stroke={chart.axis}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `${value / 1000}k`}
                  fontSize={11}
                />
                <Tooltip
                  formatter={(value) => [`${formatCurrency(value)}`, t('dashboard.montantLegend')]}
                  contentStyle={tooltipStyles}
                  cursor={{ fill: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.04)' }}
                />
                <Bar dataKey="montant" fill={chart.barGradientStart} radius={[4, 4, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </DashboardPanel>

        <DashboardPanel
          title={t('dashboard.pieDistributionTitle')}
          actions={
            <Link to="/courses" className={linkSubtle}>
              {t('dashboard.manageCourses')}
            </Link>
          }
        >
          <div className="flex h-72 items-center gap-6">
            <div className="h-full w-1/2 min-w-[140px]">
              {courseDistribution.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-slate-400">
                  {t('dashboard.noCourseData')}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={courseDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={72}
                      paddingAngle={2}
                      dataKey="value"
                      stroke="none"
                    >
                      {courseDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyles} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <ul className="min-w-0 flex-1 space-y-2.5">
              {courseDistribution.map((entry, index) => (
                <li key={index} className="flex items-center gap-2.5 text-sm">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: entry.color }} />
                  <span className="min-w-0 flex-1 truncate text-slate-600 dark:text-slate-300">{entry.name}</span>
                  <span className="font-medium tabular-nums text-slate-900 dark:text-white">
                    {totalCourses > 0 ? Math.round((entry.value / totalCourses) * 100) : 0}%
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </DashboardPanel>
      </div>

      <DashboardPanel
        title={t('dashboard.upcomingTitle')}
        subtitle={t('dashboard.upcomingSubtitle')}
        actions={
          <Link to="/schedule" className={linkSubtle}>
            {t('dashboard.seeAll')}
          </Link>
        }
      >
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />
            ))}
          </div>
        ) : upcomingCoursesData.length === 0 ? (
          <DashboardEmptyState
            icon={CalendarDaysIcon}
            title={t('dashboard.emptyUpcomingTitle')}
            body={t('dashboard.emptyUpcomingBody')}
            actionLabel={t('dashboard.scheduleCourse')}
            actionHref="/courses/add"
          />
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {upcomingCoursesData.map((course) => (
              <DashboardCourseRow
                key={course.id}
                course={course}
                language={language}
                viewLabel={t('dashboard.viewDetails')}
                studentsLabel={t('dashboard.studentsShort')}
              />
            ))}
          </div>
        )}
      </DashboardPanel>

      <DashboardPanel
        title={t('dashboard.occupancyTitle')}
        actions={
          <Link to="/classrooms" className={linkSubtle}>
            {t('dashboard.manageRooms')}
          </Link>
        }
      >
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={occupancyData} margin={{ top: 8, right: 4, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="dashboardOccFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chart.line} stopOpacity={0.2} />
                  <stop offset="100%" stopColor={chart.line} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.muted} vertical={false} />
              <XAxis dataKey="name" stroke={chart.axis} tickLine={false} axisLine={false} fontSize={11} />
              <YAxis
                stroke={chart.axis}
                tickLine={false}
                axisLine={false}
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
                fontSize={11}
              />
              <Tooltip formatter={(value) => [`${value}%`, t('dashboard.occupancyLegend')]} contentStyle={tooltipStyles} />
              <Area
                type="monotone"
                dataKey="occupancy"
                stroke={chart.line}
                strokeWidth={2}
                fill="url(#dashboardOccFill)"
                dot={false}
                activeDot={{ r: 4 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </DashboardPanel>
    </div>
  );
};

export default Dashboard;
